import React, { useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, Info, Search } from 'lucide-react';
import { validatePermission } from 'actions';
import { cooperplaceApi } from 'services/api';
import Modal from 'v3/components/Modal';
import Button from 'v3/components/Button';
import { attendancePath, kanbanReturnState, saveAttendanceSession } from './session';
import { canUseOther, otherCandidates } from './otherContract';

const REQUIREMENTS = ['Perfil da carga', 'MOPP', 'Disponibilidade'];

const canAttend = (item) =>
  validatePermission(item.type === 'PRE_LOAD' ? 'ATENDER_PRECARGA' : 'ATENDER_CARGA');

/**
 * Tela Atender com outro (5664): aberta direto pelo card ou pelo rodapé de Veículos disponíveis.
 * O backend recusa a busca quando a carga ainda não pode ser atendida com outro e devolve o motivo.
 */
export function OtherAttendanceModal({ context, show, onClose, onRefresh }) {
  const inputId = useId();
  const hintId = useId();
  const navigate = useNavigate();
  const location = useLocation();
  const [plate, setPlate] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const generation = useRef(0);
  const pending = useRef(false);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );
  const permitted = canAttend(context.item);
  const normalized = plate.trim().replace(/-/g, '').toUpperCase();
  const close = () => {
    generation.current += 1;
    pending.current = false;
    setBusy(false);
    setResult(null);
    setError(null);
    setPlate('');
    onClose();
  };
  const run = async (action) => {
    if (!permitted || pending.current) return;
    pending.current = true;
    const version = ++generation.current;
    setBusy(true);
    setError(null);
    try {
      await action(() => generation.current === version);
    } catch (failure) {
      if (generation.current === version)
        setError(
          failure?.response?.data?.message ||
            failure.message ||
            'Não foi possível consultar outro veículo.',
        );
    } finally {
      if (generation.current === version) {
        pending.current = false;
        setBusy(false);
      }
    }
  };
  const search = (event) => {
    event.preventDefault();
    if (!/^[A-Z0-9]{3,7}$/.test(normalized)) return;
    setResult(null);
    run(async (current) => {
      const response = await cooperplaceApi.get(
        `/control-tower/loads/${context.item.loadId}/other-candidates`,
        { params: { plate: normalized } },
      );
      if (current()) setResult(otherCandidates(response.data, context));
    });
  };
  // 5719: o implemento é escolhido sozinho; o cavalo e o motorista ficam para o cadastro da viagem.
  const select = (row) =>
    run(async (current) => {
      const wagon = row.assetType === 'wagon';
      const response = await cooperplaceApi.post(
        `/control-tower/loads/${context.item.loadId}/other-candidates/${wagon ? `wagons/${row.id}` : row.id}/attendance-context`,
      );
      if (!current()) return;
      const selection = response.data?.selection;
      if (
        response.data?.mode !== 'OTHER' ||
        (wagon
          ? selection?.vehicle !== null ||
            selection.driver !== null ||
            selection.wagons?.length !== 1 ||
            selection.wagons[0]?.id !== row.id
          : selection?.vehicle?.id !== row.id)
      )
        throw new Error('Contexto de atendimento com outro inválido.');
      const record = saveAttendanceSession(
        response.data,
        context,
        kanbanReturnState(location.state, context),
      );
      navigate(
        attendancePath(
          `/${record.loadKind === 'PRE_LOAD' ? 'pre-cargas' : 'cargas'}/${record.loadId}`,
          record,
        ),
      );
    });
  return (
    <Modal
      show={show}
      handleClose={close}
      size="lg"
      heading={
        <div className="tc-o-heading">
          Atender com outro
          <span className="tc-o-chip">
            {context.item.type === 'PRE_LOAD' ? 'Pré-carga' : 'Carga'} {context.item.loadId}
          </span>
        </div>
      }
      body={
        <>
          <div className="tc-o-notice">
            <Info size={16} aria-hidden="true" />
            <div>
              <p>A placa informada precisa atender a:</p>
              <ul className="tc-o-requirements">
                {REQUIREMENTS.map((label) => (
                  <li key={label}>
                    <Check size={13} aria-hidden="true" />
                    {label}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {!permitted && <p role="alert">Você não tem permissão para atender esta carga.</p>}
          <form className="tc-o-search" onSubmit={search}>
            <label htmlFor={inputId}>Placa do veículo ou implemento</label>
            <div className="tc-o-search-row">
              <div className="tc-o-input">
                <Search size={16} aria-hidden="true" />
                <input
                  id={inputId}
                  className="form-control"
                  value={plate}
                  disabled={busy || !permitted}
                  maxLength={8}
                  aria-describedby={hintId}
                  onChange={(event) => {
                    setPlate(event.target.value);
                    setResult(null);
                  }}
                  placeholder="Ex.: ABC1D23"
                />
              </div>
              <Button
                type="submit"
                disabled={busy || !permitted || !/^[A-Z0-9]{3,7}$/.test(normalized)}
              >
                Buscar placa
              </Button>
            </div>
            <small id={hintId}>Digite ao menos 3 caracteres para buscar.</small>
          </form>
          {busy && <p role="status">Validando disponibilidade e recusas…</p>}
          {error && (
            <div role="alert">
              <p>{error}</p>
              {onRefresh && (
                <Button size="sm" onClick={onRefresh}>
                  Atualizar fila
                </Button>
              )}
            </div>
          )}
          {result && (
            <div aria-live="polite">
              <p>
                {result.totalCount
                  ? `${result.totalCount} placa(s) encontrada(s).`
                  : 'Nenhum veículo ou implemento com essa placa no cadastro.'}
              </p>
              {result.totalCount > 25 && (
                <p>Exibindo as primeiras 25. Informe mais caracteres da placa.</p>
              )}
              {/* 5716: todas as placas; a incompatível fica com o rótulo e o motivo, sem seleção. */}
              <ul className="tc-o-results">
                {result.data.map((row) => {
                  const kind = row.assetType === 'vehicle' ? 'Veículo' : 'Implemento';
                  return (
                    <li key={`${row.assetType}:${row.id}`} className="tc-o-result">
                      <div>
                        <strong>
                          {[row.plate, ...row.wagons.map((w) => w.plate)].join(' / ')}
                        </strong>{' '}
                        · {kind}
                        {row.assetType === 'vehicle' &&
                          ` · ${row.driver ? row.driver.name || 'Motorista cadastrado' : 'Sem motorista'}`}
                        {!row.compatible && <small>{row.reason.message}</small>}
                      </div>
                      <div className="tc-o-result-action">
                        {!row.compatible && (
                          <span className="tc-o-incompatible">{kind} incompatível</span>
                        )}
                        <Button
                          size="sm"
                          disabled={busy || !row.compatible}
                          title={row.compatible ? undefined : row.reason.message}
                          onClick={() => row.compatible && select(row)}
                          aria-label={`Selecionar ${kind.toLowerCase()} ${row.plate}`}
                        >
                          Selecionar
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      }
      footer={
        <Button variant="secondary" onClick={close}>
          Cancelar
        </Button>
      }
    />
  );
}

/** Integração `other` do Kanban: o botão do card abre a tela direto, sem Veículos disponíveis. */
export function OtherAttendanceView({ context, onClose }) {
  return <OtherAttendanceModal context={context} show onClose={onClose} />;
}

/** Rodapé de Veículos disponíveis: libera quando todos os elegíveis em 500 km foram recusados. */
export default function OtherAttendance({ context, data, onChanged }) {
  const [show, setShow] = useState(false);
  const allowed = canAttend(context.item) && canUseOther(data?.operationalUniverse);
  return (
    <>
      {/* 5658: mesmo botão do card (vermelho, só contorno); a liberação não muda. */}
      <Button
        size="sm"
        variant="danger"
        outlined
        disabled={!allowed}
        onClick={() => setShow(true)}
        title={
          allowed
            ? 'Selecionar outro veículo'
            : 'Exige consulta completa, todas as recusas e permissão de atendimento'
        }
      >
        Atender com outro
      </Button>
      <OtherAttendanceModal
        context={context}
        show={show && allowed}
        onClose={() => setShow(false)}
        onRefresh={onChanged}
      />
    </>
  );
}
